import { observer } from 'mobx-react-lite';
import { AnimatePresence, motion } from 'motion/react';

import ArrowIcon from '@/assets/icons/arrow.svg?react';
import { classList } from '@/utils/cn';

interface ChatScrollToLastMessageButtonProps {
  show: boolean;
  /** Debounced backend-generating state from the parent; drives the busy dots. */
  isResponding?: boolean;
  onScrollToBottom(): void;
}

const DOTS = [0, 1, 2];
const CIRCLE_SIZE = '1.9rem';
const PILL_WIDTH = '2.7rem';

export const ChatScrollToLastMessageButton = observer(
  ({
    show,
    isResponding = false,
    onScrollToBottom,
  }: ChatScrollToLastMessageButtonProps) => {
    const targetWidth = isResponding ? PILL_WIDTH : CIRCLE_SIZE;

    return (
      <AnimatePresence mode="wait">
        {show && (
          <motion.button
            type="button"
            initial={{
              opacity: 0,
              scale: 0,
              width: targetWidth,
              height: CIRCLE_SIZE,
            }}
            animate={{
              opacity: 1,
              scale: 1,
              width: targetWidth,
              height: CIRCLE_SIZE,
            }}
            exit={{ opacity: 0, scale: 0 }}
            transition={{
              opacity: { type: 'spring', damping: 12 },
              scale: { type: 'spring', damping: 12 },
              width: { type: 'spring', damping: 18, stiffness: 260 },
            }}
            style={{ originX: '50%', originY: '50%', x: '50%' }}
            whileHover={{ opacity: 1, backgroundColor: 'var(--surface-hover)' }}
            whileTap={{ scale: 0.9 }}
            aria-label={
              isResponding
                ? 'Scroll to latest message. Agent is responding.'
                : 'Scroll to latest message'
            }
            className={classList(
              'cursor-default',
              'absolute right-1/2 bottom-5',
              'flex items-center justify-center',
              'bg-surface text-foreground',
              'border border-border rounded-full',
              'shadow-lg overflow-hidden'
            )}
            onClick={() => onScrollToBottom()}
          >
            <AnimatePresence mode="wait" initial={false}>
              {isResponding ? (
                <motion.div
                  key="dots"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  className="flex items-center gap-[0.1875rem]"
                >
                  {DOTS.map((i) => (
                    <span
                      key={i}
                      className="size-1 rounded-full bg-current animate-typing-dot inline-block"
                      style={{ animationDelay: `${i * 0.15}s` }}
                    />
                  ))}
                </motion.div>
              ) : (
                <motion.span
                  key="arrow"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  className="flex"
                >
                  <ArrowIcon className="text-xs rotate-180 transform-gpu" />
                </motion.span>
              )}
            </AnimatePresence>
          </motion.button>
        )}
      </AnimatePresence>
    );
  }
);
